import React, { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TradeNameSelection from './tradeNameSelection/TradeNameSelection';
import { Product } from 'src/types/ViewModel/Stowage/Product/Product';
import { SelectOption } from 'src/types/ViewModel/SelectOption';
import { useStowageStore } from 'src/context/StowageContext';
import {
    CloseIcon,
    ToggleIconButton,
    TradeNameWrapper as StyledTradeNameWrapper,
    TradeNameLabel,
    TradeNameContainer,
    TradeNameText,
    DropDownContainer,
    DropDownOptionsList,
} from './TradeNameWrapper.style';

interface TradeNameWrapperProps {
    tradeName?: string;
    onTradeNameChange: (e: SelectOption, product: Product) => void;
}

const TradeNameWrapper: React.FC<TradeNameWrapperProps> = ({ tradeName, onTradeNameChange }) => {
    const { isTradeNameSelectionListOpen, setIsTradeNameSelectionListOpen } = useStowageStore();
    const dropdownRef = useRef<HTMLDivElement>(null);

    const onOpenTradeNameSelectionList = (e: React.MouseEvent): void => {
        e.stopPropagation();
        setIsTradeNameSelectionListOpen(!isTradeNameSelectionListOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsTradeNameSelectionListOpen(false);
            }
        };

        // Add click event listener when the dropdown is open
        if (isTradeNameSelectionListOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Clean up the event listener when component unmounts or dropdown closes
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isTradeNameSelectionListOpen, setIsTradeNameSelectionListOpen]);

    return (
        <StyledTradeNameWrapper className="field-wrapper">
            <TradeNameLabel>Trade name</TradeNameLabel>
            <TradeNameContainer ref={dropdownRef}>
                <div onClick={(e) => onOpenTradeNameSelectionList(e)}>
                    {!isTradeNameSelectionListOpen && <TradeNameText>{tradeName}</TradeNameText>}
                </div>
                {isTradeNameSelectionListOpen && (
                    <DropDownOptionsList>
                        <DropDownContainer>
                            <CloseIcon onClick={(e) => onOpenTradeNameSelectionList(e)}>
                                <FontAwesomeIcon icon="chevron-up" />
                            </CloseIcon>
                            <TradeNameSelection onClick={onTradeNameChange} />
                        </DropDownContainer>
                    </DropDownOptionsList>
                )}
                <ToggleIconButton
                    onClick={(e) => onOpenTradeNameSelectionList(e)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onOpenTradeNameSelectionList(e);
                        }
                    }}
                    className="focus-input"
                    role="button"
                    aria-expanded={isTradeNameSelectionListOpen}
                    aria-label="Toggle trade name selection"
                    tabIndex={1}
                >
                    <FontAwesomeIcon icon={isTradeNameSelectionListOpen ? 'chevron-up' : 'chevron-down'} />
                </ToggleIconButton>
            </TradeNameContainer>
        </StyledTradeNameWrapper>
    );
};

export default TradeNameWrapper;
